import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
  IconAlertTriangle,
  IconChevronLeft,
  IconDownload,
  IconHelpCircle,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchUsers } from '@/reducers/organizations/organizationUsers.actions'
import { loadPlaybooks } from '@/reducers/playbooks/playbooks.actions'
import { uploadUserCSV } from '@/reducers/organizations/organizationUsersCSV.actions'
import { fetchOrganizationTags } from '@/reducers/organizations/organizationTags.actions'
import {
  clearCsvUploadResponse,
  clearParsedCsv,
  setParsedCsv,
} from '@/reducers/organizations/organizationUsersCSV.redux'

import { CsvDropzone } from './CsvDropzone'
import { FieldGuide } from './FieldGuide'
import parseCSVData, { headersWhiteList } from './helpers'
import { downloadCsvTemplate, toTitleCase } from '../CsvInstructions/helpers'
import {
  FEATURE_GATED_PERMISSIONS,
  STATUS_ERROR,
  STATUS_WARNING,
  TAG_REGEX,
  buildValidationContext,
  validateRow,
} from './validation'

import './PrepareAndPreview.scss'

const TEMPLATE_FILENAME = 'balto_bulk_user_upload_template.csv'
const INVALID_ROWS_FILENAME = 'invalid_rows.csv'
// Above this row count the in-browser validation + table render starts to
// noticeably lag, so warn the user. There is no hard cap.
const LARGE_FILE_THRESHOLD = 5000

// Object-keyed so the example values stay aligned with the column headers
// regardless of which columns we filter out for feature-gating. Previously
// these were positional arrays and a misalignment was silently shifting
// values into the wrong columns (`desired_username` got example data meant
// for `tools_editor`).
const BASE_TEMPLATE_ROWS = [
  {
    first_name: 'Wile',
    last_name: 'E Coyote',
    username: 'roadrunner@acme.com',
    playbook: 'Acme - Outbound Anvil Sales',
    voip_user_id: '214901042915436',
    external_system_id: '1234567',
    balto_cloud_access: 'yes',
    playbook_edit_access: 'yes',
    user_management_access: 'no',
    user_status: 'active',
    uncategorized_tags: 'cartoon, road_runner',
    edit_qa: 'yes',
    edit_leaderboard: 'yes',
    realtime_coaching_access: 'standard',
    midcall_playbook_switching_enabled: 'yes',
    desktop_enterprise_enabled: 'yes',
    edit_qa_copilot: 'no',
    hierarchy_manager: 'yes',
    screen_capture_enabled: 'no',
    agent_gpt_access: 'yes',
    tools_editor: 'no',
    newrez_complaints_enabled: 'no',
    outbound_campaign_editor: 'yes',
    togo_editor: 'yes',
    desired_username: 'wile.coyote@newacme.com',
  },
  {
    first_name: 'Bugs',
    last_name: 'Bunny',
    username: 'doc@acme.com',
    playbook: 'Acme - Inbound Anvil Sales',
    voip_user_id: '',
    external_system_id: 'abc-1234',
    balto_cloud_access: 'yes',
    playbook_edit_access: 'no',
    user_management_access: 'no',
    user_status: 'active',
    uncategorized_tags: 'cartoon, bunny',
    edit_qa: 'yes',
    edit_leaderboard: 'yes',
    realtime_coaching_access: 'admin',
    midcall_playbook_switching_enabled: 'yes',
    desktop_enterprise_enabled: 'yes',
    edit_qa_copilot: 'yes',
    hierarchy_manager: 'yes',
    screen_capture_enabled: 'yes',
    agent_gpt_access: 'yes',
    tools_editor: 'yes',
    newrez_complaints_enabled: 'no',
    outbound_campaign_editor: 'no',
    togo_editor: 'no',
    desired_username: 'bugs@warnerbros.com',
  },
  {
    first_name: 'Tweety',
    last_name: 'Bird',
    username: 'bird@acme.com',
    playbook: 'Acme - Bird House Sales',
    voip_user_id: '1234141',
    external_system_id: '',
    balto_cloud_access: 'no',
    playbook_edit_access: 'no',
    user_management_access: 'no',
    user_status: 'deactivated',
    uncategorized_tags: 'cartoon, bird, tiny',
    edit_qa: 'yes',
    edit_leaderboard: 'yes',
    realtime_coaching_access: 'standard',
    midcall_playbook_switching_enabled: 'yes',
    desktop_enterprise_enabled: 'yes',
    edit_qa_copilot: 'no',
    hierarchy_manager: 'yes',
    screen_capture_enabled: 'no',
    agent_gpt_access: 'yes',
    tools_editor: 'no',
    newrez_complaints_enabled: 'no',
    outbound_campaign_editor: 'no',
    togo_editor: 'no',
    desired_username: 'tweety@xhitter.com',
  },
]

// A column is feature-gated if it (or its alias) appears in
// FEATURE_GATED_PERMISSIONS. `complaints_enabled` is a legacy alias for
// `newrez_complaints_enabled` — both share the same org flag.
const featureFlagForHeader = (header) => {
  if (FEATURE_GATED_PERMISSIONS[header]) {
    return FEATURE_GATED_PERMISSIONS[header].flag
  }
  if (header === 'complaints_enabled') {
    return FEATURE_GATED_PERMISSIONS.newrez_complaints_enabled.flag
  }
  return null
}

export const filterHeadersByOrgFeatures = (headers, organizationFeatureFlags = {}) =>
  headers.filter((header) => {
    const flag = featureFlagForHeader(header)
    return flag === null || organizationFeatureFlags[flag]
  })

const buildTemplateData = (tagCategories, organizationFeatureFlags = {}) => {
  const tagCategoryNames = tagCategories.map((category) => category.name)
  const allowedHeaders = filterHeadersByOrgFeatures(headersWhiteList, organizationFeatureFlags)
  const headers = [...allowedHeaders, ...tagCategoryNames]
  const rows = BASE_TEMPLATE_ROWS.map((row) => [
    ...allowedHeaders.map((header) => row[header] ?? ''),
    ...tagCategoryNames.map(() => ''),
  ])
  return [headers, ...rows]
}

const VIEW_UPLOAD = 'upload'
const VIEW_GUIDE = 'guide'

const TAB_VALID = 'valid'
const TAB_INVALID = 'invalid'

// Map the verbose backend message to a short, user-friendly label. The full
// raw message is still surfaced in the hover tooltip so support can debug.
const friendlyServerError = (rawMessage) => {
  const message = rawMessage || ''
  const patterns = [
    [/duplicate username/i, 'Username already exists'],
    [/duplicate tag/i, 'Duplicate tag assignment'],
    [/invalid playbook/i, 'Unknown playbook'],
    [/invalid first name/i, 'Missing first name'],
    [/invalid last name/i, 'Missing last name'],
    [/invalid username/i, 'Invalid username'],
    [/wrong category/i, 'Tag is in the wrong category'],
    [/cannot enable screen capture/i, 'Screen capture not allowed'],
    [/invalid desired_username/i, 'Invalid new email'],
    [/failed to send email/i, 'Could not send invite email'],
    [/^failed to save user/i, 'Could not save user'],
  ]
  const match = patterns.find(([pattern]) => pattern.test(message))
  if (match) {
    return match[1]
  }
  return message.length > 60 ? `${message.slice(0, 60)}…` : message
}

// Strip any internal-only field (underscore-prefixed) plus rowProps before
// shipping a row to the backend. New internal fields added later don't need
// to update this function as long as they keep the _-prefix convention.
const stripInternalFields = (row) =>
  Object.fromEntries(
    Object.entries(row).filter(([key]) => !key.startsWith('_') && key !== 'rowProps')
  )

const PrepareAndPreview = ({
  tagCategories = [],
  modalClose,
  onUploadComplete,
  onImportingChange,
  organizationFeatureFlags,
}) => {
  const dispatch = useDispatch()
  const params = useParams()

  const playbooks = useSelector((state) => state.playbooks.playbooks)
  const organizationTags = useSelector((state) => state.organizationTags)
  const organizationUsersCSV = useSelector((state) => state.organizationUsersCSV)
  const userOrganizationId = useSelector((state) => state.currentUser?.organizationid)
  const organizationid = params.organizationid || userOrganizationId
  const streamingComplete = useSelector((state) => state.organizationUsersCSV.streamingComplete)
  const uploadResponse = useSelector((state) => state.organizationUsersCSV.uploadResponse)

  const [view, setView] = useState(VIEW_UPLOAD)
  const [file, setFile] = useState(null)
  const [activeTab, setActiveTab] = useState(TAB_VALID)
  const [isImporting, setIsImporting] = useState(false)
  const [submittedUsernames, setSubmittedUsernames] = useState([])
  const [loadingPreviewUsers, setLoadingPreviewUsers] = useState(false)

  const isImportingRef = useRef(false)
  isImportingRef.current = isImporting

  useEffect(() => {
    if (onImportingChange) {
      onImportingChange(isImporting)
    }
  }, [isImporting, onImportingChange])

  useEffect(() => {
    if (!organizationid) {
      return
    }
    if (isEmpty(organizationTags)) {
      dispatch(fetchOrganizationTags({ organizationId: organizationid }))
    }
    if (isEmpty(playbooks)) {
      // Scope the fetch to the target org. Validation needs to reject a row
      // whose playbook exists somewhere else in Balto but not in this org —
      // the backend filters by Config.organization_id, so we match.
      dispatch(loadPlaybooks({ requestedProperties: 'name', organizationId: organizationid }))
    }
    dispatch(
      fetchUsers({
        queryParam: 'all',
        setLoading: setLoadingPreviewUsers,
        organizationId: organizationid,
        dispatchAction: 'organizations/updatePreviewUsers',
      })
    )
  }, [organizationid])

  useEffect(() => {
    if (!streamingComplete || !isImportingRef.current) {
      return
    }
    const userResultRows = uploadResponse.slice(1).filter((row) => row[0] === 'user')
    const successes = userResultRows.filter((row) => row[1] === 'True').length
    const failures = userResultRows.filter((row) => row[1] === 'False').length
    const shouldClose = failures === 0

    if (shouldClose) {
      toast.success(`Imported ${successes} user${successes === 1 ? '' : 's'}`)
    } else {
      toast.warning(
        `Imported ${successes} user${successes === 1 ? '' : 's'} — ${failures} row${failures === 1 ? '' : 's'} failed`
      )
      // Server-failed rows are now classified as errors, so the Invalid tab
      // surfaces them with the specific server error message.
      setActiveTab(TAB_INVALID)
      // The just-imported users need to be in previewUsers so a re-upload in
      // the same session correctly marks them as existing (update) instead of
      // brand-new rows. Refresh quietly — no loader on this re-validate path.
      if (organizationid && successes > 0) {
        dispatch(
          fetchUsers({
            queryParam: 'all',
            organizationId: organizationid,
            dispatchAction: 'organizations/updatePreviewUsers',
          })
        )
      }
    }

    setIsImporting(false)
    if (onUploadComplete) {
      onUploadComplete({ shouldClose })
    }
  }, [streamingComplete, uploadResponse, onUploadComplete, dispatch, organizationid])

  // Map each submitted row to its server-side result. The backend yields one
  // "user" row per submitted user in submission order, so we can zip them by
  // index instead of parsing usernames out of message strings.
  const serverResultsByUsername = useMemo(() => {
    if (isEmpty(submittedUsernames) || !streamingComplete) {
      return new Map()
    }
    const userResultRows = uploadResponse.slice(1).filter((row) => row[0] === 'user')
    const map = new Map()
    submittedUsernames.forEach((username, index) => {
      const result = userResultRows[index]
      if (!result) {
        return
      }
      map.set(username, { success: result[1], message: result[2] })
    })
    return map
  }, [submittedUsernames, streamingComplete, uploadResponse])

  const hasImportResults = serverResultsByUsername.size > 0 && streamingComplete

  const handleFileSelected = ({ file: selectedFile, csvText }) => {
    setFile(selectedFile)
    setActiveTab(TAB_VALID)
    setSubmittedUsernames([])
    // Clear any prior upload response so a new file in the same modal session
    // doesn't see stale streamingComplete/uploadResponse from the previous run.
    dispatch(clearCsvUploadResponse())
    const parsed = parseCSVData(csvText, tagCategories)
    dispatch(setParsedCsv({ ...parsed, tagCategories }))
  }

  const handleFileCleared = () => {
    setFile(null)
    setSubmittedUsernames([])
    dispatch(clearParsedCsv())
  }

  const { tags, users, headers, configNames, previewUsers: existingUsers } = organizationUsersCSV

  const hasFile = Boolean(file)

  const validationContext = useMemo(
    () =>
      buildValidationContext({
        organizationTags,
        playbooks,
        tagCategories,
        existingUsers,
        users,
        organizationFeatureFlags,
      }),
    [organizationTags, playbooks, tagCategories, existingUsers, users, organizationFeatureFlags]
  )

  const validations = useMemo(
    () => users.map((user) => validateRow(user, validationContext)),
    [users, validationContext]
  )

  // Memoise the row-enrichment + split. Without this, every render
  // (tab switch, isImporting flip, every Redux selector identity change)
  // re-walked the full users array and allocated N fresh objects — at
  // 50k rows that's noticeable lag on every interaction. Split valid /
  // invalid in the same pass instead of two extra O(N) filters.
  const { validRows, invalidRows } = useMemo(() => {
    const valid = []
    const invalid = []
    users.forEach((user, index) => {
      const validation = validations[index]
      const username = user.username?.toLowerCase()
      const serverResult = username ? serverResultsByUsername.get(username) : null
      const serverFailed = serverResult?.success === 'False'
      const displayStatus = serverFailed ? STATUS_ERROR : validation.status
      const row = {
        ...user,
        _status: displayStatus,
        _statusReasons: [...validation.errors, ...validation.warnings],
        rowProps: {
          className: classNames({
            'csv-preview-row--error': displayStatus === STATUS_ERROR,
            'csv-preview-row--warning': displayStatus === STATUS_WARNING,
          }),
        },
      }
      if (displayStatus === STATUS_ERROR) {
        invalid.push(row)
      } else {
        valid.push(row)
      }
    })
    return { validRows: valid, invalidRows: invalid }
  }, [users, validations, serverResultsByUsername])

  const validCount = validRows.length
  const invalidCount = invalidRows.length

  const invalidFileLevelTags = tags.filter((item) => TAG_REGEX.test(item.tag))
  const invalidConfigNames = configNames.filter(
    (configName) => !validationContext.existingPlaybookNames.has(configName)
  )

  const rowNumberColumn = {
    accessor: '_csvRowNumber',
    label: 'Row',
  }

  const validationColumn = {
    accessor: '_status',
    label: 'Row validation',
    format: (value, row) => {
      const username = row.username?.toLowerCase()
      const serverResult = username ? serverResultsByUsername.get(username) : null
      if (serverResult) {
        if (serverResult.success === 'True') {
          return (
            <Pill success small tooltip={serverResult.message}>
              imported
            </Pill>
          )
        }
        if (serverResult.success === 'Warning') {
          return (
            <Pill warning small tooltip={serverResult.message}>
              warning
            </Pill>
          )
        }
        return (
          <span className="csv-preview__validation-error" title={serverResult.message}>
            {friendlyServerError(serverResult.message)}
          </span>
        )
      }
      if (value === STATUS_ERROR) {
        const reasons = row._statusReasons.join(' • ')
        return (
          <span className="csv-preview__validation-error" title={reasons}>
            {reasons}
          </span>
        )
      }
      if (value === STATUS_WARNING) {
        return (
          <Pill warning small tooltip={row._statusReasons.join(' • ')}>
            update
          </Pill>
        )
      }
      return (
        <Pill success small>
          valid
        </Pill>
      )
    },
  }

  const dataHeaders = headers.map((header) => ({
    accessor: header,
    label: header === 'username' ? 'email' : toTitleCase(header),
  }))

  const tableColumns = [rowNumberColumn, ...dataHeaders, validationColumn]

  const handleDownloadInvalidRows = () => {
    const rows = invalidRows.map((row) => headers.map((header) => row[header] ?? ''))
    downloadCsvTemplate([headers, ...rows], INVALID_ROWS_FILENAME)
  }

  const handleImport = () => {
    const cleanUsers = validRows.map(stripInternalFields)
    // Only ship tags the backend needs to create. The endpoint reads `newTags`
    // and ignores `tags` entirely, so we can drop the full list from the
    // payload (was ~tens of KB for large CSVs).
    const newTags = tags.filter(
      (item) => !TAG_REGEX.test(item.tag) && !validationContext.existingTagNames.has(item.tag)
    )
    setSubmittedUsernames(cleanUsers.map((user) => user.username?.toLowerCase()).filter(Boolean))
    setIsImporting(true)
    dispatch(clearCsvUploadResponse())
    dispatch(
      uploadUserCSV({
        users: cleanUsers,
        newTags,
        configNames: configNames.filter((name) =>
          validationContext.existingPlaybookNames.has(name)
        ),
        organizationid,
      })
    )
  }

  const visibleRows = activeTab === TAB_INVALID ? invalidRows : validRows

  let submitDisabledTooltip
  if (!hasFile) {
    submitDisabledTooltip = 'Upload a CSV file to continue'
  } else if (isEmpty(users)) {
    submitDisabledTooltip = 'No valid rows in this file'
  } else if (validCount === 0) {
    submitDisabledTooltip = 'No valid rows to import'
  }

  let stepLabel
  if (!isImporting) {
    stepLabel = hasFile ? 'Review data' : 'Upload file'
  }

  const isGuide = view === VIEW_GUIDE
  const showPreview = hasFile && !loadingPreviewUsers && !isEmpty(users)

  return (
    <div className="prepare-preview" data-testid="prepare-and-preview">
      <div className="prepare-preview__scroll">
        <div className="prepare-preview__intro">
          <h2 className="prepare-preview__title">Upload your users via CSV</h2>
          <p className="prepare-preview__subtitle muted-text">
            Add or update users in bulk. Download the template, fill in the rows, then drop the file
            below — we&apos;ll show you a preview before anything is saved.
          </p>
          <div className="prepare-preview__intro-actions flex-align-center small-gap">
            <Button
              small
              onClick={() =>
                downloadCsvTemplate(
                  buildTemplateData(tagCategories, organizationFeatureFlags),
                  TEMPLATE_FILENAME
                )
              }
              data-testid="download-template"
            >
              <IconDownload />
              Download CSV Template
            </Button>
            <Button
              small
              onClick={() => setView(isGuide ? VIEW_UPLOAD : VIEW_GUIDE)}
              data-testid="toggle-field-guide"
            >
              {isGuide ? <IconChevronLeft /> : <IconHelpCircle />}
              {isGuide ? 'Back to Upload' : 'View Column Guide'}
            </Button>
          </div>
        </div>

        {isGuide ? (
          <FieldGuide
            tagCategories={tagCategories}
            organizationFeatureFlags={organizationFeatureFlags}
          />
        ) : (
          <div className="prepare-preview__body flex-column gap">
            <CsvDropzone
              file={file}
              onFileSelected={handleFileSelected}
              onFileCleared={handleFileCleared}
            />

            {hasFile && loadingPreviewUsers && <Loader size="large" centered />}

            {hasFile && !loadingPreviewUsers && isEmpty(users) && (
              <div
                className="prepare-preview__empty flex-align-center small-gap"
                data-testid="preview-empty"
              >
                <IconAlertTriangle className="icon-svg status-warning" />
                <span>
                  No valid rows found in this file. Check the header row matches the template and
                  that the file isn&apos;t empty.
                </span>
              </div>
            )}

            {showPreview && (
              <div className="prepare-preview__preview flex-column small-gap">
                <h3 className="prepare-preview__preview-heading">Preview</h3>

                {users.length > LARGE_FILE_THRESHOLD && (
                  <div
                    className="prepare-preview__perf-warn flex-align-center small-gap"
                    data-testid="large-file-warning"
                  >
                    <IconAlertTriangle className="icon-svg status-warning" />
                    <span>
                      {`This file has ${users.length.toLocaleString()} rows — validation and import may take a moment.`}
                    </span>
                  </div>
                )}

                {invalidCount > 0 && (
                  <div className="prepare-preview__warn-banner" data-testid="invalid-rows-banner">
                    <div className="prepare-preview__warn-text">
                      <div className="prepare-preview__warn-headline flex-align-center small-gap">
                        <IconAlertTriangle className="icon-svg status-warning" />
                        <strong>
                          {hasImportResults
                            ? `${invalidCount.toLocaleString()} row${invalidCount === 1 ? '' : 's'} couldn't be imported.`
                            : `${invalidCount.toLocaleString()} row${invalidCount === 1 ? '' : 's'} won't be imported.`}
                        </strong>
                      </div>
                      <p className="prepare-preview__warn-body muted-text">
                        Download the invalid rows so you can fix them later. You can re-upload them
                        in a new session anytime.
                      </p>
                    </div>
                    <Button
                      basic
                      small
                      onClick={handleDownloadInvalidRows}
                      data-testid="download-invalid-rows"
                    >
                      <IconDownload />
                      <span>Download invalid rows</span>
                    </Button>
                  </div>
                )}

                {(!isEmpty(invalidFileLevelTags) || !isEmpty(invalidConfigNames)) && (
                  <div className="prepare-preview__file-issues flex-column small-gap">
                    {!isEmpty(invalidFileLevelTags) && (
                      <span className="flex-align-center small-gap">
                        <IconAlertTriangle className="icon-svg status-critical" />
                        <strong>Invalid tags:</strong>
                        <span>{invalidFileLevelTags.map((item) => item.tag).join(', ')}</span>
                      </span>
                    )}
                    {!isEmpty(invalidConfigNames) && (
                      <span className="flex-align-center small-gap">
                        <IconAlertTriangle className="icon-svg status-critical" />
                        <strong>Unknown playbooks:</strong>
                        <span>{invalidConfigNames.join(', ')}</span>
                      </span>
                    )}
                  </div>
                )}

                <SegmentedControl
                  className="prepare-preview__tabs"
                  items={[
                    {
                      accessor: TAB_VALID,
                      label: (
                        <span className="flex-align-center small-gap">
                          Valid rows
                          <Pill xs informative>
                            {validCount}
                          </Pill>
                        </span>
                      ),
                      active: activeTab === TAB_VALID,
                      onClick: () => setActiveTab(TAB_VALID),
                      dataTestId: 'tab-valid-rows',
                    },
                    {
                      accessor: TAB_INVALID,
                      label: (
                        <span className="flex-align-center small-gap">
                          Invalid rows
                          <Pill xs informative>
                            {invalidCount}
                          </Pill>
                        </span>
                      ),
                      active: activeTab === TAB_INVALID,
                      onClick: () => setActiveTab(TAB_INVALID),
                      dataTestId: 'tab-invalid-rows',
                    },
                  ]}
                />

                <AdvancedTable
                  rows={visibleRows}
                  columns={tableColumns}
                  index="_csvRowNumber"
                  defaultOrderBy="_csvRowNumber"
                  defaultOrder="asc"
                  pagination
                  rowsPerPage={100}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="prepare-preview__footer flex-align-center">
        <Button onClick={modalClose} disabled={isImporting} data-testid="prepare-preview-cancel">
          {hasImportResults ? 'Close' : 'Cancel'}
        </Button>
        <div className="prepare-preview__footer-spacer" />
        {stepLabel && !hasImportResults && (
          <span className="prepare-preview__step-count muted-text">{stepLabel}</span>
        )}
        {!hasImportResults && (
          <Button
            primary
            loading={isImporting}
            disabled={!hasFile || validCount === 0 || isImporting}
            disabledTooltip={submitDisabledTooltip}
            onClick={handleImport}
            data-testid="prepare-preview-submit"
          >
            {isImporting
              ? `Importing ${validCount} user${validCount === 1 ? '' : 's'}...`
              : `Import ${validCount} user${validCount === 1 ? '' : 's'}`}
          </Button>
        )}
      </div>
    </div>
  )
}

export default PrepareAndPreview
